import { useState } from "react";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { DeleteButton } from "../../../components/NewButtons";
import { DropdownItemButton } from "../../common/DropdownItemButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ConfirmActionModal } from "../../../components/ConfirmActionModal";
import { useUpdateMenuRecommendedProducts } from "../service/menus-api";
import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";
import {
  MenuRecommendedProduct,
  UpdateRecommendedProductsPayload,
} from "../models/Menus";
import { getErrorMessage } from "../../../error-handler";
import { toastr } from "react-redux-toastr";
import { ActionSpinner } from "../../../components/ActionSpinner";

type Props = {
  id?: string;
  menuId?: string;
  recommendedProducts: MenuRecommendedProduct[];
};

export const RemoveRecommendedProductSelector = ({
  id,
  menuId,
  recommendedProducts,
}: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const {
    mutateAsync: updateRecommendedProducts,
    isLoading: isUpdating,
  } = useUpdateMenuRecommendedProducts(menuId);

  const openModal = () => {
    setModalOpen(true);
  };

  return (
    <div>
      <DropdownItemButton
        onClick={openModal}
        icon={<FontAwesomeIcon icon={solid("trash-can")} />}
      >
        Remove product
      </DropdownItemButton>
      <ConfirmActionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Are you sure?"
        subtitle="Removing the product means that it won't be recommended to users when they go to checkout. OBS: it can take up to 10 minutes for changes to take effect in the app, website and kiosk."
        confirmButton={
          <DeleteButton
            onClick={async () => {
              try {
                const request: UpdateRecommendedProductsPayload = {
                  organizationKeyName,
                  menuId,
                  recommendedProducts: recommendedProducts
                    .filter((product) => product.id !== id)
                    .map((product) => ({
                      id: product.product.id,
                      sortKey: product.sortKey,
                      type: product.type,
                    })),
                };
                await updateRecommendedProducts(request);
                toastr.success("Success", "Product removed successfully");
              } catch (error: unknown) {
                const message = getErrorMessage(error);
                toastr.error("Error", message);
              } finally {
                setModalOpen(false);
              }
            }}
          >
            {isUpdating ? <ActionSpinner /> : "Remove product"}
          </DeleteButton>
        }
      />
    </div>
  );
};
